/**
 * Workflow-run status data-source function.
 *
 * Reads `Retry-After` and falls back to ~2s; this is the only place the poll
 * cadence is decided, so a later LRO `/operations` migration stays local.
 */
import type { WorkflowRunStatus } from "~/api/generated/workflow-service/model";
import { getWorkflowRunStatus } from "~/api/generated/workflow-service/workflow-runs/workflow-runs";

import { WorkflowRunAuthError } from "../lib/errorCatalog";
import { readEnvelope } from "./envelope";

const RETRY_AFTER_FALLBACK_MS = 2000;

/**
 * A non-OK answer from the run-status endpoint (404-pending and 401-auth are
 * classified separately). Carries the HTTP status so the poll loop can tell
 * transient server trouble (5xx / 429 — keep polling; observed live when a
 * cold-restarted record-service made workflow-service 500 mid-run) from a
 * request that can never succeed (other 4xx — fail fast).
 */
export class RunStatusFetchError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Workflow run status fetch failed with status ${String(status)}`);
    this.name = "RunStatusFetchError";
    this.status = status;
  }
}

export const isTransientStatusError = (error: unknown): error is RunStatusFetchError =>
  error instanceof RunStatusFetchError && (error.status >= 500 || error.status === 429);

export interface RunStatusResult {
  run: WorkflowRunStatus;
  /** Next poll delay in ms, honouring `Retry-After` when present. */
  retryAfterMs: number;
}

/** Parse an HTTP `Retry-After` header (delta-seconds or HTTP-date) to ms. */
const parseRetryAfter = (headers: Headers): number => {
  const raw = headers.get("Retry-After");

  if (!raw) {
    return RETRY_AFTER_FALLBACK_MS;
  }

  const seconds = Number(raw);

  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }

  const dateMs = Date.parse(raw);

  if (!Number.isNaN(dateMs)) {
    return Math.max(0, dateMs - Date.now());
  }

  return RETRY_AFTER_FALLBACK_MS;
};

/**
 * Reads a run's status, returning `null` while the run is not yet readable.
 *
 * Submit answers `202 Accepted` — the run is queued, not created — and the
 * record only becomes queryable once the submitted event has been published.
 * That publish has been observed taking seconds on a cold service, so the first
 * poll routinely lands on a `404`. Treating it as an error turns a normal race
 * into a user-visible failure for a run that then succeeds.
 */
export const fetchRunStatusOrPending = async (
  runId: string,
  signal?: AbortSignal,
): Promise<RunStatusResult | null> => {
  const response = await getWorkflowRunStatus(runId, { signal });
  // The generated client types `status` as the success literal only, so compare
  // through a widened local — otherwise the 404 branch narrows the rest away.
  const status: number = response.status;

  if (status === 404) {
    return null;
  }

  // apiClient already tried a token refresh before returning this response
  // (see `executeRequest`) — a 401 here means the session itself is dead, not
  // that this particular run is unreadable. Classify it so callers can show
  // "sign in again" instead of a bare fetch-failed string.
  if (status === 401) {
    throw new WorkflowRunAuthError();
  }

  if (status !== 200) {
    throw new RunStatusFetchError(status);
  }

  return {
    run: readEnvelope<WorkflowRunStatus>(response),
    retryAfterMs: parseRetryAfter(response.headers),
  };
};

export const fetchRunStatus = async (
  runId: string,
  signal?: AbortSignal,
): Promise<RunStatusResult> => {
  const result = await fetchRunStatusOrPending(runId, signal);

  if (!result) {
    throw new Error(`Workflow run status fetch failed with status 404`);
  }

  return result;
};
